import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

// Distinct sentinel so `undefined` (a common "no filter" value) round-trips
// through JSON, which can't represent it directly.
const UNDEFINED_SENTINEL = '__undefined__';

/**
 * Like `useState`, but the value is mirrored to `sessionStorage` under `key`.
 * Scoped to the browser tab: it survives client-side navigation (e.g. opening a
 * detail page and coming back) and a tab refresh, and is cleared when the tab
 * closes. Use it to keep list filters/sort/view from resetting on navigation.
 */
export function useSessionState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) return initial;
      if (raw === UNDEFINED_SENTINEL) return undefined as T;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, value === undefined ? UNDEFINED_SENTINEL : JSON.stringify(value));
    } catch {
      // sessionStorage unavailable or full — persistence is best-effort.
    }
  }, [key, value]);

  return [value, setValue];
}
